import React, { useMemo, useState, createElement } from 'react';
import { Send, Clock, Users, CheckCircle, Upload, Mail, X, AlertTriangle } from 'lucide-react';
import { ScheduleModal } from '../components/ScheduleModal';
import { Toast } from '../components/Toast';
interface NotificationHistoryItem {
  id: number;
  title: string;
  message: string;
  sent: string;
  recipients: number;
  opened: number;
  isSegmented: boolean;
  status?: 'sent' | 'scheduled';
  scheduledFor?: string;
}
const initialNotificationHistory: NotificationHistoryItem[] = [{
  id: 1,
  title: 'Lembre-se dos seus sonhos!',
  message: 'Não desista! Seus objetivos estão mais perto do que você imagina.',
  sent: 'Há 2 horas',
  recipients: 5234,
  opened: 3847,
  isSegmented: false,
  status: 'sent'
}, {
  id: 2,
  title: 'Nova categoria disponível',
  message: 'Confira a nova categoria Bem-estar e organize melhor seus sonhos!',
  sent: 'Há 1 dia',
  recipients: 5180,
  opened: 2890,
  isSegmented: false,
  status: 'sent'
}, {
  id: 3,
  title: 'Parabéns pelo progresso!',
  message: 'Você completou 5 passos esta semana. Continue assim!',
  sent: 'Há 3 dias',
  recipients: 3200,
  opened: 2400,
  isSegmented: false,
  status: 'sent'
}, {
  id: 4,
  title: 'Atualização importante',
  message: 'Confira as novidades da versão 2.0 do iDreamy!',
  sent: 'Há 5 dias',
  recipients: 150,
  opened: 120,
  isSegmented: true,
  status: 'sent'
}];
// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function Notifications() {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [recipientType, setRecipientType] = useState<'all' | 'specific'>('all');
  const [emailList, setEmailList] = useState('');
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [notificationHistory, setNotificationHistory] = useState<NotificationHistoryItem[]>(initialNotificationHistory);
  const [cancelConfirmId, setCancelConfirmId] = useState<number | null>(null);
  // Parse and validate emails
  const parsedEmails = useMemo(() => {
    if (!emailList.trim()) return [];
    // Split by comma or newline
    const emails = emailList.split(/[\n,]+/).map(email => email.trim()).filter(email => email.length > 0);
    // Remove duplicates
    const uniqueEmails = Array.from(new Set(emails));
    return uniqueEmails.map(email => ({
      email,
      isValid: EMAIL_REGEX.test(email)
    }));
  }, [emailList]);
  const validEmailCount = parsedEmails.filter(e => e.isValid).length;
  const invalidEmailCount = parsedEmails.filter(e => !e.isValid).length;
  const handleImportList = () => {
    // Placeholder for file upload functionality
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.txt';
    input.onchange = e => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        // In real implementation, would parse the file
        console.log('File selected:', file.name);
      }
    };
    input.click();
  };
  const handleScheduleConfirm = (date: string, time: string) => {
    // Format the scheduled date/time for display
    const [year, month, day] = date.split('-');
    const scheduledDateTime = `${day}/${month}/${year} às ${time}`;
    // Calculate recipient count
    const recipientCount = recipientType === 'all' ? 5234 : validEmailCount;
    // Create new scheduled notification
    const newNotification: NotificationHistoryItem = {
      id: Date.now(),
      title: title || 'Sem título',
      message: message || 'Sem mensagem',
      sent: '',
      recipients: recipientCount,
      opened: 0,
      isSegmented: recipientType === 'specific',
      status: 'scheduled',
      scheduledFor: scheduledDateTime
    };
    // Add to history at the top
    setNotificationHistory([newNotification, ...notificationHistory]);
    // Clear form
    setTitle('');
    setMessage('');
    setRecipientType('all');
    setEmailList('');
    // Close modal and show toast
    setIsScheduleModalOpen(false);
    setToastMessage('✓ Notificação agendada com sucesso!');
    setShowToast(true);
  };
  const handleSendNow = () => {
    // Validate form
    if (!title || !message) {
      alert('Por favor, preencha o título e a mensagem');
      return;
    }
    if (recipientType === 'specific' && validEmailCount === 0) {
      alert('Por favor, adicione pelo menos um e-mail válido');
      return;
    }
    // In real implementation, would send the notification
    console.log('Sending notification now:', {
      title,
      message,
      recipientType,
      validEmailCount
    });
    // Show success feedback
    setToastMessage('✓ Notificação enviada com sucesso!');
    setShowToast(true);
    // Clear form
    setTitle('');
    setMessage('');
    setRecipientType('all');
    setEmailList('');
  };
  const handleScheduleClick = () => {
    // Validate form
    if (!title || !message) {
      alert('Por favor, preencha o título e a mensagem');
      return;
    }
    if (recipientType === 'specific' && validEmailCount === 0) {
      alert('Por favor, adicione pelo menos um e-mail válido');
      return;
    }
    setIsScheduleModalOpen(true);
  };
  const handleCancelSchedule = (id: number) => {
    setCancelConfirmId(id);
  };
  const confirmCancelSchedule = () => {
    if (cancelConfirmId) {
      // Remove the notification from history
      setNotificationHistory(notificationHistory.filter(n => n.id !== cancelConfirmId));
      // Show toast
      setToastMessage('Agendamento cancelado com sucesso');
      setShowToast(true);
      // Close confirmation modal
      setCancelConfirmId(null);
    }
  };
  return <div className="space-y-6" data-id="element-379">
      <div data-id="element-380">
        <h1 className="text-3xl font-bold text-gray-900" data-id="element-381">Notificações Push</h1>
        <p className="text-gray-600 mt-1" data-id="element-382">
          Crie e envie notificações para os usuários
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-383">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-384">
          Nova Notificação
        </h2>
        <div className="space-y-4" data-id="element-385">
          <div data-id="element-386">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-387">
              Título da Notificação
            </label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Ex: Lembre-se dos seus sonhos!" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8BC4C3] focus:border-transparent" data-id="element-388" />
          </div>

          <div data-id="element-389">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-390">
              Mensagem
            </label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Digite a mensagem da notificação..." rows={4} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8BC4C3] focus:border-transparent resize-none" data-id="element-391" />
          </div>

          <div data-id="element-392">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-393">
              Destinatários
            </label>
            <div className="space-y-3" data-id="element-394">
              <div className="flex flex-col gap-2" data-id="element-395">
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-396">
                  <input type="radio" name="recipientType" value="all" checked={recipientType === 'all'} onChange={e => setRecipientType(e.target.value as 'all')} className="w-4 h-4 text-[#8BC4C3] focus:ring-[#8BC4C3]" data-id="element-397" />
                  <span className="text-sm text-gray-700" data-id="element-398">
                    Todos os usuários
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-399">
                  <input type="radio" name="recipientType" value="specific" checked={recipientType === 'specific'} onChange={e => setRecipientType(e.target.value as 'specific')} className="w-4 h-4 text-[#8BC4C3] focus:ring-[#8BC4C3]" data-id="element-400" />
                  <span className="text-sm text-gray-700" data-id="element-401">
                    Grupo específico por e-mails
                  </span>
                </label>
              </div>

              {recipientType === 'specific' && <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-402">
                  <div className="relative" data-id="element-403">
                    <textarea value={emailList} onChange={e => setEmailList(e.target.value)} placeholder="Digite ou cole os e-mails, separados por vírgula ou quebra de linha" rows={5} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8BC4C3] focus:border-transparent resize-none font-mono text-sm" style={{
                  minHeight: '120px'
                }} data-id="element-404" />
                  </div>

                  <div className="flex items-center justify-between" data-id="element-405">
                    <div className="flex items-center gap-2" data-id="element-406">
                      <Mail className="w-4 h-4 text-gray-400" data-id="element-407" />
                      <span className="text-sm text-gray-600" data-id="element-408">
                        <span className="font-semibold text-[#8BC4C3]" data-id="element-409">
                          {validEmailCount}
                        </span>{' '}
                        e-mails válidos encontrados
                        {invalidEmailCount > 0 && <span className="text-red-600 ml-2" data-id="element-410">
                            ({invalidEmailCount} inválido
                            {invalidEmailCount !== 1 ? 's' : ''})
                          </span>}
                      </span>
                    </div>
                    <button onClick={handleImportList} className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm" data-id="element-411">
                      <Upload className="w-4 h-4" data-id="element-412" />
                      Importar lista (.csv, .txt)
                    </button>
                  </div>

                  {invalidEmailCount > 0 && <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-id="element-413">
                      <p className="text-sm text-red-700" data-id="element-414">
                        ⚠️ Alguns e-mails possuem formato inválido. Por favor,
                        verifique e corrija antes de enviar.
                      </p>
                    </div>}
                </div>}
            </div>
          </div>

          <div data-id="element-415">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-416">
              Agendamento
            </label>
            <div className="flex gap-4" data-id="element-417">
              <button onClick={handleSendNow} className="flex-1 px-4 py-2 bg-[#8BC4C3] text-white rounded-lg font-medium hover:bg-[#6BABA9] transition-colors flex items-center justify-center gap-2" data-id="element-418">
                <Send className="w-4 h-4" data-id="element-419" />
                Enviar Agora
              </button>
              <button onClick={handleScheduleClick} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2" data-id="element-420">
                <Clock className="w-4 h-4" data-id="element-421" />
                Agendar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200" data-id="element-422">
        <div className="p-6 border-b border-gray-200" data-id="element-423">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-424">
            Histórico de Notificações
          </h2>
        </div>
        <div className="overflow-x-auto" data-id="element-425">
          <table className="w-full" data-id="element-426">
            <thead className="bg-gray-50 border-b border-gray-200" data-id="element-427">
              <tr data-id="element-428">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-429">
                  Título
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-430">
                  Mensagem
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-431">
                  Enviado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-432">
                  Destinatários
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-433">
                  Taxa de Abertura
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-434">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-435">
              {notificationHistory.map(notification => <tr key={notification.id} className="hover:bg-gray-50 transition-colors" data-id="element-436">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-437">
                    <p className="text-sm font-medium text-gray-900" data-id="element-438">
                      {notification.title}
                    </p>
                  </td>
                  <td className="px-6 py-4" data-id="element-439">
                    <p className="text-sm text-gray-600 max-w-md truncate" data-id="element-440">
                      {notification.message}
                    </p>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-441">
                    {notification.status === 'scheduled' ? <div className="flex items-center gap-2" data-id="element-442">
                        <Clock className="w-4 h-4 text-orange-500" data-id="element-443" />
                        <div data-id="element-444">
                          <p className="text-sm text-orange-600 font-medium" data-id="element-445">
                            Agendada para {notification.scheduledFor}
                          </p>
                          <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 rounded" data-id="element-446">
                            AGENDADA
                          </span>
                        </div>
                      </div> : <span className="text-sm text-gray-600" data-id="element-447">
                        {notification.sent}
                      </span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-448">
                    <div className="flex items-center gap-2" data-id="element-449">
                      {notification.isSegmented ? <>
                          <Mail className="w-4 h-4 text-[#8BC4C3]" data-id="element-450" />
                          <span className="text-sm text-gray-900" data-id="element-451">
                            Lista (
                            {notification.recipients.toLocaleString('pt-BR')}{' '}
                            destinatários)
                          </span>
                        </> : <>
                          <Users className="w-4 h-4 text-gray-400" data-id="element-452" />
                          <span className="text-sm text-gray-900" data-id="element-453">
                            {notification.recipients.toLocaleString('pt-BR')}
                          </span>
                        </>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-454">
                    {notification.status === 'scheduled' ? <span className="text-sm text-gray-400" data-id="element-455">—</span> : <div className="flex items-center gap-2" data-id="element-456">
                        <CheckCircle className="w-4 h-4 text-green-500" data-id="element-457" />
                        <span className="text-sm text-gray-900" data-id="element-458">
                          {(notification.opened / notification.recipients * 100).toFixed(1).replace('.', ',')}
                          %
                        </span>
                      </div>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-459">
                    {notification.status === 'scheduled' ? <button onClick={() => handleCancelSchedule(notification.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors" title="Cancelar agendamento" data-id="element-460">
                        <X className="w-4 h-4" data-id="element-461" />
                      </button> : <span className="text-sm text-gray-400" data-id="element-462">—</span>}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      <ScheduleModal isOpen={isScheduleModalOpen} onClose={() => setIsScheduleModalOpen(false)} onConfirm={handleScheduleConfirm} data-id="element-463" />

      {/* Cancel Confirmation Modal */}
      {cancelConfirmId && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-in fade-in duration-200" data-id="element-464">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 animate-in slide-in-from-bottom-4 duration-300" data-id="element-465">
            <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-466">
              <div className="flex items-center gap-3" data-id="element-467">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center" data-id="element-468">
                  <AlertTriangle className="w-5 h-5 text-red-600" data-id="element-469" />
                </div>
                <h2 className="text-xl font-bold text-gray-900" data-id="element-470">
                  Cancelar Agendamento
                </h2>
              </div>
              <button onClick={() => setCancelConfirmId(null)} className="p-1 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-471">
                <X className="w-5 h-5" data-id="element-472" />
              </button>
            </div>

            <div className="p-6" data-id="element-473">
              <p className="text-gray-600" data-id="element-474">
                Tem certeza que deseja cancelar este agendamento? Esta ação não
                pode ser desfeita.
              </p>
            </div>

            <div className="flex gap-3 p-6 border-t border-gray-200" data-id="element-475">
              <button onClick={() => setCancelConfirmId(null)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" data-id="element-476">
                Não, manter
              </button>
              <button onClick={confirmCancelSchedule} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium" data-id="element-477">
                Sim, cancelar
              </button>
            </div>
          </div>
        </div>}

      <Toast message={toastMessage} isVisible={showToast} onClose={() => setShowToast(false)} data-id="element-478" />
    </div>;
}